import { useState } from "react";
import type { Contact, MessageEvent } from "@veto/domain";
import { ModalShell } from "./ModalShell";

export const SUPPORT_EMAIL = "support@martialtech.fr";

/**
 * Report & block (phone parity; App Store 1.2 / Play UGC): blocks the contact on this device and
 * opens an email to support with only what the reporter chooses to include. Veto can't read
 * chats, so nothing reaches us unless that email is sent.
 */
export function ReportModal({
  contact,
  messages,
  reported,
  onClose,
  onConfirm,
}: {
  contact: Contact;
  /** This chat's (decrypted) messages, for the optional evidence. */
  messages: MessageEvent[];
  reported?: MessageEvent | null;
  onClose: () => void;
  /** Block (or decline the request and block); resolves once done. */
  onConfirm: () => Promise<void>;
}) {
  const [include, setInclude] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    let evidence = include
      ? messages.filter((m) => !m.deletedForEveryone && !m.deletedForMe).slice(-10)
      : [];
    if (reported && !evidence.some((m) => m.eventId === reported.eventId)) {
      evidence = [...evidence, reported].sort((a, b) => a.createdAt - b.createdAt);
    }
    await onConfirm();
    const lines = [
      "Report about a Veto contact",
      "",
      `Name shown: ${contact.displayName}`,
      `Identity key: ${contact.messagingPublicKeyHex}`,
      "",
      "What happened (please describe):",
      "",
      "",
    ];
    if (evidence.length) {
      lines.push("Messages I chose to include:");
      for (const m of evidence) {
        const mark = reported && m.eventId === reported.eventId ? ">> REPORTED " : "";
        lines.push(
          `${mark}[${new Date(m.createdAt).toISOString()}] ${m.direction === "out" ? "me" : contact.displayName}: ${m.body}`,
        );
      }
    }
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Veto report — ${contact.displayName}`)}&body=${encodeURIComponent(lines.join("\n"))}`;
    window.location.href = href;
    onClose();
  }

  return (
    <ModalShell
      title={reported ? "Report this message?" : `Report ${contact.displayName}?`}
      ariaLabel="Report and block"
      onClose={onClose}
      size="sm"
      className="confirm-card"
      footer={
        <div className="actions-row">
          <button type="button" className="btn secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn danger"
            onClick={() => void submit()}
            disabled={busy}
          >
            Report &amp; block
          </button>
        </div>
      }
    >
      <p className="confirm-body">
        {reported ? "The message is included in your report. " : ""}
        {contact.displayName} will be blocked on this device. Veto is end-to-end encrypted, so we
        only see what you choose to send us.
      </p>
      <label className="report-include">
        <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} />
        Include the last 10 messages from this chat
      </label>
    </ModalShell>
  );
}
